/**
 * Pane tile registry — maps each tile `kind` to how it is drawn.
 *
 * The kinds are the server's component vocabulary
 * (`backend/omni/modules/pane/components.py`), reached through the
 * generated client: `TILE_REGISTRY` is a full `Record<TileKind, …>`, so a
 * kind the server accepts and this build does not draw fails typecheck.
 * Agents fill these shapes through `core.pane_mount` — generative UI as
 * data, drawn by these components; no agent HTML reaches the browser.
 *
 * Size and placement are not here: the server sizes a new tile to its
 * content and places it (`components.default_size`, `find_slot`), so
 * there is one size table, not two that drift.
 *
 * Spec: Docs/architecture/24-omni-pane.md §4.
 */
import type * as React from "react";
import {
  BarChart3,
  CheckSquare,
  FileText,
  Hash,
  Image as ImageIcon,
  LayoutList,
  Link2,
  PlayCircle,
  Square,
  Table2,
  type LucideIcon,
} from "lucide-react";
import { CardTile } from "./tiles/CardTile";
import { FallbackTile } from "./tiles/FallbackTile";
import { KpiEditor } from "./tiles/KpiTile";
import { LEAF_BODIES } from "./tiles/leaves";
import { MarkdownEditor } from "./tiles/MarkdownTile";
import type { TileKind } from "./types";

export interface TileDefinition {
  /** Display name shown in the Add menu and tile chrome. */
  title: string;
  Icon: LucideIcon;
  /** Tile body. Receives the tile's `data` and its `w/h`. */
  Body: React.ComponentType<TileBodyProps>;
  /** Flush body = no inner padding (the video renders to the edges). */
  flush?: boolean;
  /** Inline editor; when present a pencil button appears in the chrome. */
  Editor?: React.ComponentType<TileEditorProps>;
  /**
   * Offered in the Add menu. Only kinds a person can fill by hand: an
   * empty table or chart with no editor would be a dead box.
   */
  addable?: boolean;
}

export interface TileBodyProps {
  data: Record<string, unknown>;
  w: number;
  h: number;
  /**
   * Write new `data` for this tile (the pane's OCC PATCH). Bodies that
   * are worked in place — a checklist being ticked — call it; the rest
   * ignore it. Absent where the body is a read-only preview.
   */
  onChange?: (data: Record<string, unknown>) => void;
}

/**
 * Props for a tile's inline editor. `onSave` receives a partial patch
 * (title and/or data) that the pane sends through the OCC PATCH path.
 * Editors send only their kind's own fields: the server refuses unknown
 * ones (`extra="forbid"`), so spreading an old payload back would fail.
 */
export interface TileEditorProps {
  data: Record<string, unknown>;
  title: string;
  onSave: (patch: { title?: string; data?: Record<string, unknown> }) => void;
  onCancel: () => void;
}

export const TILE_REGISTRY: Record<TileKind, TileDefinition> = {
  markdown: {
    title: "Notes",
    Icon: FileText,
    Body: LEAF_BODIES.markdown,
    Editor: MarkdownEditor,
    addable: true,
  },
  checklist: { title: "Checklist", Icon: CheckSquare, Body: LEAF_BODIES.checklist, addable: true },
  kpi: { title: "Metric", Icon: Hash, Body: LEAF_BODIES.kpi, Editor: KpiEditor, addable: true },
  table: { title: "Table", Icon: Table2, Body: LEAF_BODIES.table },
  chart: { title: "Chart", Icon: BarChart3, Body: LEAF_BODIES.chart },
  image: { title: "Image", Icon: ImageIcon, Body: LEAF_BODIES.image },
  video: { title: "Video", Icon: PlayCircle, Body: LEAF_BODIES.video, flush: true },
  links: { title: "Links", Icon: Link2, Body: LEAF_BODIES.links },
  card: { title: "Card", Icon: LayoutList, Body: CardTile },
};

/**
 * Always returns a renderable definition: a row written before the
 * vocabulary (a `text` or `bar` tile) draws as the FallbackTile.
 */
export function resolveTile(kind: string): TileDefinition {
  const found = (TILE_REGISTRY as Record<string, TileDefinition | undefined>)[kind];
  if (found) return found;
  return { title: kind, Icon: Square, Body: FallbackTile };
}

/** The Add menu: kinds a person can fill by hand, by title. */
export function listTileKinds(): Array<{ kind: TileKind; def: TileDefinition }> {
  return (Object.entries(TILE_REGISTRY) as Array<[TileKind, TileDefinition]>)
    .filter(([, def]) => def.addable)
    .map(([kind, def]) => ({ kind, def }))
    .sort((a, b) => a.def.title.localeCompare(b.def.title));
}
